// frontend/src/utils/sanitize.js
import DOMPurify from 'dompurify';

// Replace only angle brackets that wrap an email address
export function encodeEmailBrackets(str = '') {
  return str.replace(
    /<\s*([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})\s*>/gi,
    '&lt;$1&gt;'
  );
}

// Grab an email address from a "Name <user@domain>" or plain "user@domain"
export function extractEmailAddress(str = '') {
  const m = str.match(/([^\s<>"']+@[^\s<>"']+)/);
  return m ? m[1] : '';
}

/**
 * Sanitizes HTML with a strict allowlist.
 * - allows basic markup incl. <img>
 * - restricts href/src/srcset to http(s), mailto, data:image;base64, or common public folders
 * - adds rel/target to anchors
 * - keeps inline style (you can remove 'style' from ALLOWED_ATTR if you prefer)
 */
const ALLOWED_URI = /^(?:(?:https?:|mailto:|data:image\/[a-z0-9.+-]+;base64,)|(?:\/|\.\/|\.\.\/)?(?:images|img|static|assets)\/)/i;

const CONFIG = {
  ALLOWED_TAGS: [
    'a', 'b', 'i', 'u', 'strong', 'em', 'p', 'br',
    'ul', 'ol', 'li', 'span', 'div',
    'table', 'thead', 'tbody', 'tr', 'td', 'th',
    'img', 'button'
  ],
  ALLOWED_ATTR: [
    'class', 'href', 'src', 'srcset', 'alt', 'title',
    'width', 'height', 'target', 'style'
  ],
  FORBID_TAGS: [
    'script', 'iframe', 'object', 'embed',
    'form', 'input', 'link', 'meta', 'svg', 'math'
  ],
  RETURN_TRUSTED_TYPE: false
};

// Install hooks once
let hooksInstalled = false;
function installHooksOnce() {
  if (hooksInstalled) return;
  hooksInstalled = true;

  // Validate href/src/srcset against ALLOWED_URI
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    const name = data.attrName;
    const val = String(data.attrValue || '');

    if (name === 'href' || name === 'src') {
      if (!ALLOWED_URI.test(val)) data.keepAttr = false;
    }

    if (name === 'srcset') {
      // srcset can contain multiple URLs with descriptors: "url1 1x, url2 2x"
      const cleaned = val
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(entry => {
          const [url, descriptor] = entry.split(/\s+/, 2);
          return ALLOWED_URI.test(url) ? entry : null;
        })
        .filter(Boolean)
        .join(', ');
      if (cleaned) {
        data.attrValue = cleaned;
        data.keepAttr = true;
      } else {
        data.keepAttr = false;
      }
    }
  });

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
    ['href', 'src', 'srcset'].forEach((attr) => {
      const v = node.getAttribute(attr);
      if (v && !ALLOWED_URI.test(v)) node.removeAttribute(attr);
    });
  });
}

export function sanitizeHtml(dirty = '') {
  if (typeof dirty !== 'string') return '';
  installHooksOnce();

  // Encode <user@domain> so it can't be mistaken for an HTML tag
  const encoded = encodeEmailBrackets(dirty);

  return DOMPurify.sanitize(encoded, CONFIG);
}
